import React, { Suspense, lazy, useRef, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import usePortrait from '../../utils/usePortrait';
import useFleetStore from '../../stores/useFleetStore';
import useUserStore from '../../stores/useUserStore';
import useDashboardConfigStore from '../../stores/useDashboardConfigStore';
import { useApi } from '../../apiHelpers';
import { useToast } from '../../components/Toast/ToastContext';
import HexKeyboard from '../../HomeScreen/HexKeyboard';
import GoFlagPopup from '../../HomeScreen/GoFlagPopup';
import { scanDestination } from '../../utils/scanDestination';
import { buttonClass } from '../../components/ui/Button';
import Panel from '../../components/ui/Panel';
import Alert from '../../components/ui/Alert';
import LogoSpinner from '../../components/ui/LogoSpinner';
import Tooltip from '../../components/ui/Tooltip';
import { fieldBase } from '../../components/review/fieldStyles';

// Lazy-load the camera scanner — keeps the ~150KB ZXing bundle out of the
// dashboard's main chunk; only downloads when a user opens the QR scanner.
const BarcodeScannerModal = lazy(() => import('../../components/BarcodeScanner/BarcodeScannerModal'));

/**
 * A drone sticker encodes the full dashed serial (printing-utils.js
 * printSquareQRLabels), and it is the ONE format worth recognizing here: the
 * fleet store already holds every serial, so matching it locally costs nothing.
 *
 * Everything else goes to POST /api/scanner/lookup and is routed by the `kind`
 * it comes back as. That is deliberate. Board stickers carry a
 * `<station_type>:<uid>` prefix from fireflasher's Zebra, and station_type is a
 * Python enum that grows (AP, AP_UBLOX, AP_MULTI, ESC, BAT, CHG, AUTO) — any
 * list of prefixes kept over here goes stale the next time a jig is added. The
 * server owns that knowledge; this widget just asks.
 */
const DRONE_SERIAL_RE = /^[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}$/i;

/**
 * Longest input still treated as last-5 shorthand. Past this the field stops
 * auto-dashing and takes what you typed verbatim, so a pasted serial or board
 * UID survives instead of being reformatted into nonsense.
 */
const SHORTHAND_MAX = 5;

interface ScanResult {
  boardType: string;
  uid: string;
  serialnumber: string;
  /** Set when the sticker's prefix disagrees with the board's type in the DB. */
  labelMismatch: {
    sticker_prefix: string;
    sticker_implies: string;
    board_type: string;
  } | null;
}

/**
 * DroneLookupModule — serial lookup for drones AND boards.
 *
 * Owns all its state: serial input, drone matching, popup toggles, HexKeyboard
 * visibility. HexKeyboard and popups use position:fixed, so they escape the
 * DashboardModule wrapper. During edit mode, HexKeyboard is hidden to avoid
 * z-index conflicts with EditModeToolbar.
 *
 * Scan outcomes:
 *   drone sticker           → straight to the drone page (you already know which drone)
 *   board sticker, paired   → the serial, in place, with Go / Flag armed
 *   board sticker, no drone → /board/:uid, which says so and shows the history
 *   part sticker (GPS/LED/PYR) → /part/:id, the unit's page
 *   asset sticker           → /assets/:id
 *
 * The hex keyboard starts CLOSED and is opened by the keyboard button (or, on
 * portrait where the input is readOnly, by tapping the input). It used to open
 * on mount, which covered half a phone screen before anyone asked it to.
 */
const DroneLookupModule: React.FC = () => {
  const user = useUserStore((state: any) => state.user);
  const drones = useFleetStore((state: any) => state.drones);
  const editMode = useDashboardConfigStore((state: any) => state.editMode);
  const isPortrait = usePortrait();
  const history = useHistory();
  const api = useApi();
  const toast = useToast();

  const [qrActive, setQRActive] = useState(false);
  const [goFlagPopup, setGoFlagPopup] = useState(false);
  const [serialGo, setSerialGo] = useState('');
  const [serialG2Short, setSerialG2Short] = useState('');
  const [droneGo, setDroneGo] = useState('');
  const [goButtonActive, setGoButtonActive] = useState(false);
  const [showHexKeyboard, setShowHexKeyboard] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [resolving, setResolving] = useState(false);
  // Bumped by any input edit or new scan. A lookup that lands after its
  // generation has moved on is stale and must not write to the field — the
  // camera modal closes on scan, so the user can start typing during the
  // round trip and would otherwise watch their input get overwritten.
  const lookupGeneration = useRef(0);

  const togglePopup = (stateUpdater: React.Dispatch<React.SetStateAction<boolean>>) => {
    stateUpdater((prev) => !prev);
  };

  /**
   * Arm Go / Flag against a full serial. `serialGo` feeds GoFlagPopup, which
   * matches with endsWith() — a full serial endsWith itself, so passing the
   * whole thing is correct, not merely tolerated. Does NOT touch the visible
   * field; each caller decides what the box should read.
   */
  const armDrone = (serialnumber: string) => {
    setDroneGo(serialnumber);
    setSerialGo(serialnumber);
    setGoButtonActive(true);
  };

  /** The last-5 form the hex keyboard produces, e.g. F020-020B-0001-3210 → 1-3210. */
  const toShorthand = (serialnumber: string) => serialnumber.slice(-8).replace(/^000/, '');

  /** Last-5 shorthand: match on the tail, exactly as the hex keyboard always has. */
  const applyShorthand = (compact: string) => {
    const formatted = compact.length >= 2 ? `${compact[0]}-${compact.slice(1)}` : compact;
    setSerialG2Short(formatted);
    setSerialGo(`000${formatted}`);

    // An empty field must arm nothing. Without this guard the suffix collapses
    // to plain "000" and matches any drone whose serial happens to end in it —
    // there is one in the fleet today — so clearing the box would silently arm
    // Go and Flag against an unrelated drone.
    const matchedDrone = formatted
      ? drones.find((drone: any) => drone.serialnumber?.endsWith(`000${formatted}`))
      : null;

    if (matchedDrone) {
      setDroneGo(matchedDrone.serialnumber);
      setGoButtonActive(true);
    } else {
      setGoButtonActive(false);
    }
  };

  /**
   * Anything longer than the shorthand: a pasted serial, a typed board UID, a
   * wedge scanner's output. Shown verbatim rather than auto-dashed, because
   * `${raw[0]}-${raw.slice(1)}` turns a 24-char UID into garbage.
   *
   * A complete drone serial arms immediately — the store already has it, so it
   * costs nothing. A board UID waits for Enter; resolving it needs the network,
   * and firing that on every keystroke of a 24-char paste is 24 requests.
   */
  const applyFullCode = (text: string) => {
    const value = text.toUpperCase();
    setSerialG2Short(value);

    const matched = DRONE_SERIAL_RE.test(value)
      ? drones.find((d: any) => d.serialnumber?.toUpperCase() === value)
      : null;

    if (matched) {
      armDrone(matched.serialnumber);
    } else {
      setGoButtonActive(false);
    }
  };

  /**
   * One entry point for the camera, the wedge and the Enter key.
   *
   * Drone serials are matched locally; everything else is handed to
   * /api/scanner/lookup and routed by the `kind` it answers with. That is what
   * makes an unfamiliar sticker prefix a server-side concern rather than a
   * dashboard bug, and it is why asset codes work here without extra code.
   */
  const resolveCode = async (raw: string) => {
    const value = (raw || '').trim();
    if (!value) return;

    if (DRONE_SERIAL_RE.test(value)) {
      const matched = drones.find(
        (d: any) => d.serialnumber?.toUpperCase() === value.toUpperCase()
      );
      if (!matched) {
        toast.warning('Serial Number not registered!');
        return;
      }
      history.push(`/drone/${matched.serialnumber}`);
      return;
    }

    const generation = ++lookupGeneration.current;
    setResolving(true);
    try {
      const { kind, payload } = await api.post('/scanner/lookup', { body: { raw: value } });
      if (generation !== lookupGeneration.current) return; // superseded

      // A board with no drone behind it falls through to the board page,
      // which says so; a paired one arms Go / Flag here instead.
      if (kind !== 'board' || !payload.serialnumber) {
        const to = scanDestination({ kind, payload });
        if (to) history.push(to);
        else toast.warning('Nothing here matches that code.');
        return;
      }

      setSerialG2Short(toShorthand(payload.serialnumber));
      armDrone(payload.serialnumber);
      setScanResult({
        boardType: payload.board_type,
        uid: payload.primary_uid,
        serialnumber: payload.serialnumber,
        labelMismatch: payload.label_mismatch || null,
      });
    } catch (err: any) {
      if (generation !== lookupGeneration.current) return; // superseded
      if (err?.status === 404) {
        toast.warning('Nothing registered under that code.');
      } else {
        toast.error('Could not look that up. Try again.');
      }
    } finally {
      if (generation === lookupGeneration.current) setResolving(false);
    }
  };

  /**
   * Shorthand below SHORTHAND_MAX, full code above it. A colon forces full-code
   * mode at any length, since only a sticker prefix or a MAC contains one.
   */
  const handleInputChange = (text: string) => {
    setScanResult(null);
    lookupGeneration.current += 1; // any edit supersedes a pending lookup
    const compact = text.replace(/[-:\s]/g, '');

    if (compact.length <= SHORTHAND_MAX && !text.includes(':')) {
      applyShorthand(compact.toUpperCase());
    } else {
      applyFullCode(text);
    }
  };

  const handleGo = () => {
    history.push(`/drone/${droneGo}`);
  };

  const iconButton =
    'bg-surface-container-highest border-l border-outline-variant text-on-surface px-3.5 py-2.5 ' +
    'transition-all duration-200 inline-flex items-center justify-center ' +
    'hover:text-primary hover:shadow-[0_0_16px_rgba(255,204,51,0.12)]';

  return (
    <div className="max-w-[700px] mx-auto">
      <Panel label="Drone lookup" className="h-full">
        <div className="flex flex-1 flex-col items-center justify-center gap-3">
          {/* HexKeyboard — opt-in via the keyboard button. Hidden during edit
              mode to avoid a z-index conflict with the toolbar. */}
          {showHexKeyboard && !editMode && (
            <HexKeyboard
              serialG2Short={serialG2Short}
              updateSerialGen2Go={handleInputChange}
              togglePopup={() => setShowHexKeyboard(false)}
            />
          )}

          {/* Joined input + keyboard + QR: shared border, no gap, single rounded outline */}
          <div className="flex items-stretch">
            <input
              type="text"
              className={`text-center py-2.5 px-4 rounded-l-md ${fieldBase} font-mono !text-lg tracking-wider relative focus:z-10 w-48`}
              placeholder="LAST 5 OR ID"
              value={serialG2Short}
              onChange={(e) => handleInputChange(e.target.value)}
              readOnly={isPortrait}
              aria-busy={resolving}
              // On portrait the field is readOnly (the OS keyboard has no hex
              // row worth using), so a tap has nothing to do unless it opens
              // the one keyboard that does.
              onClick={() => { if (isPortrait) setShowHexKeyboard(true); }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                // Armed means a drone is already matched, locally and for free.
                // Otherwise Enter is the "go look this up" gesture for a board
                // UID or asset code that needs the network.
                if (goButtonActive) handleGo();
                else resolveCode(serialG2Short);
              }}
            />
            <Tooltip label="Hex keyboard"><button
              className={`${iconButton} ${showHexKeyboard ? 'text-primary' : ''}`}
              onClick={() => setShowHexKeyboard((prev) => !prev)}
              aria-label="Toggle hex keyboard"
              aria-pressed={showHexKeyboard}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                <rect x="2" y="6" width="20" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="2" />
                <rect x="5.5" y="9" width="2" height="2" fill="currentColor" />
                <rect x="9.5" y="9" width="2" height="2" fill="currentColor" />
                <rect x="13.5" y="9" width="2" height="2" fill="currentColor" />
                <rect x="17" y="9" width="1.5" height="2" fill="currentColor" />
                <rect x="5.5" y="12.5" width="2" height="2" fill="currentColor" />
                <rect x="9.5" y="12.5" width="2" height="2" fill="currentColor" />
                <rect x="13.5" y="12.5" width="2" height="2" fill="currentColor" />
                <rect x="17" y="12.5" width="1.5" height="2" fill="currentColor" />
                <rect x="7" y="15.5" width="10" height="1.5" rx="0.5" fill="currentColor" />
              </svg>
            </button></Tooltip>
            <Tooltip label="Scan a drone or board QR code"><button
              className={`${iconButton} rounded-r-md`}
              onClick={() => togglePopup(setQRActive)}
              aria-label="Scan a drone or board QR code"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
                {/* Three finder patterns: outlined rounded square with a filled inner pip */}
                <rect x="3" y="3" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
                <rect x="6" y="6" width="2" height="2" fill="currentColor" />
                <rect x="13" y="3" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
                <rect x="16" y="6" width="2" height="2" fill="currentColor" />
                <rect x="3" y="13" width="8" height="8" rx="1" fill="none" stroke="currentColor" strokeWidth="2" />
                <rect x="6" y="16" width="2" height="2" fill="currentColor" />
                {/* Data cells in the bottom-right quadrant */}
                <rect x="13" y="13" width="2" height="2" fill="currentColor" />
                <rect x="17" y="13" width="2" height="2" fill="currentColor" />
                <rect x="15" y="15" width="2" height="2" fill="currentColor" />
                <rect x="13" y="17" width="2" height="2" fill="currentColor" />
                <rect x="19" y="17" width="2" height="2" fill="currentColor" />
                <rect x="15" y="19" width="2" height="2" fill="currentColor" />
                <rect x="19" y="19" width="2" height="2" fill="currentColor" />
              </svg>
            </button></Tooltip>
          </div>

          {resolving && (
            <div className="flex items-center gap-1.5 text-xs text-on-surface-variant" role="status">
              <LogoSpinner size="inline" />
              Looking that up...
            </div>
          )}

          {/* Board scan provenance — without this the serial appears in the
              field with no explanation of where it came from. */}
          {scanResult && (
            <Panel on="panel" padding="sm" label="Scanned board" className="w-full max-w-sm text-center">
              <div className="text-[11px] uppercase tracking-widest text-on-surface-variant">
                {scanResult.boardType} board
                <span className="font-mono normal-case tracking-normal text-on-surface-variant ml-1.5">
                  ...{scanResult.uid.slice(-6)}
                </span>
              </div>
              <div className="font-mono text-base text-primary mt-0.5">{scanResult.serialnumber}</div>
              {scanResult.labelMismatch && (
                <Alert variant="note" tone="warning" className="mt-1.5 text-left">
                  Sticker says {scanResult.labelMismatch.sticker_prefix}, this board is
                  {' '}{scanResult.labelMismatch.board_type}. Check the label is on the right hardware.
                </Alert>
              )}
              <Link
                to={`/board/${scanResult.uid}`}
                className="text-[11px] text-on-surface-variant hover:text-primary transition-colors"
              >
                Board details
              </Link>
            </Panel>
          )}

          <div className="flex items-center justify-center gap-3 mt-1">
            {goButtonActive ? (
              <>
                <Link
                  to={`/drone/${droneGo}`}
                  className={buttonClass('good', 'lg')}
                >
                  Go!
                </Link>
                <button
                  className={buttonClass('accent', 'lg')}
                  onClick={() => togglePopup(setGoFlagPopup)}
                >
                  Flag
                </button>
              </>
            ) : (
              <>
                <button
                  className={buttonClass('quiet', 'lg')}
                  disabled
                >
                  Go!
                </button>
                <button
                  className={buttonClass('quiet', 'lg')}
                  disabled
                >
                  Flag
                </button>
              </>
            )}
          </div>

          {user.firstname === 'Connor' && (
            <Link
              className={`${buttonClass('accent', 'lg')} mt-1`}
              to="/connorstars"
            >
              ISSUE STARS
            </Link>
          )}
        </div>
      </Panel>

      {/* Popups — position:fixed, escape the module wrapper naturally */}
      {goFlagPopup && (
        <GoFlagPopup
          serialnumber={serialGo}
          togglePopup={() => togglePopup(setGoFlagPopup)}
        />
      )}
      {qrActive && (
        <Suspense fallback={null}>
          <BarcodeScannerModal
            open={qrActive}
            onClose={() => setQRActive(false)}
            onScan={({ text }: { text: string }) => resolveCode(text)}
            formats={['qr']}
            title="Scan Drone or Board QR"
            hint="Point camera at a drone or board sticker"
          />
        </Suspense>
      )}
    </div>
  );
};

export default DroneLookupModule;
